import type { FC } from "react";
import { useMemo } from "react";

import { useConfig } from "@jti/app-context/useConfig";
import { Card } from "@jti/core-components/components/Card";
import type { OrderItem } from "@jti/magento";
import { useTranslations } from "@jti/ui";

import SummaryCartBundleItem from "@checkout/components/SummaryCartItems/SummaryCartBundleItem";
import SummaryCartItem from "@checkout/components/SummaryCartItems/SummaryCartItem";
import { useCartItemsGrouping, useFreeTrial } from "@checkout/hooks";
import getCartItemsCount from "@checkout/utils/getCartItemsCount";

import SummaryCartStyles from "../../../../components/SummaryCartItems/SummaryCartItems.module.scss";
import styles from "./SummaryItems.module.scss";

type SummaryItemsProps = {
  items: OrderItem[];
  showProductList?: boolean;
};

export const SummaryItems: FC<SummaryItemsProps> = ({ items, showProductList = true }) => {
  const { __ } = useTranslations();
  const config = useConfig();
  const { isFreeTrial } = useFreeTrial();
  const { payableItems, groupedGiftItems } = useCartItemsGrouping(items);
  const orderItems = useMemo(() => [...payableItems, ...groupedGiftItems], [payableItems, groupedGiftItems]);

  if (!items.length) {
    return null;
  }

  const itemsCount = getCartItemsCount(items);

  return (
    <div className={styles.container}>
      <Card
        variant="level-1"
        title={__("cart.order.summary.items.title", [itemsCount])}
        showSeparator={true}
        isCollapsible={true}
        defaultCollapsed={false}
        data-testid="SummaryItems"
      >
        {showProductList && (
          <div role="list" className={SummaryCartStyles.list}>
            {orderItems.map((item) =>
              (item.type_id === "bundle" || item.product_type === "bundle") && config.displayBundleSelectedItems ? (
                <SummaryCartBundleItem {...item} key={item.sku} isFreeTrial={isFreeTrial} />
              ) : (
                <SummaryCartItem {...item} key={item.sku} isFreeTrial={isFreeTrial} />
              )
            )}
          </div>
        )}
      </Card>
    </div>
  );
};
